import { useTranslation } from "@sagot/i18n";
import { Badge, Skeleton, cn } from "@sagot/ui";
import { useState, type DragEvent } from "react";
import { LeadCard } from "./lead-card";
import { stageTone } from "./lead-logic";
import type { BoardColumn, Lead, Stage } from "./lead-types";

const DRAG_TYPE = "application/x-sagot-lead";

interface LeadsBoardProps {
  id: string;
  columns: readonly BoardColumn[] | undefined;
  isPending: boolean;
  canEdit: boolean;
  /** Optimistic stage per lead id while a move is in flight. */
  overrides: Readonly<Record<string, string>>;
  leadHref: (leadId: string) => string;
  onMove: (lead: Lead, stage: Stage) => void;
}

/** Newest first, ties by id: refetches never reshuffle cards under the pointer mid-drag. */
const byNewest = (a: Lead, b: Lead) => b.created_at.localeCompare(a.created_at) || a.id.localeCompare(b.id);

function regroup(columns: readonly BoardColumn[], overrides: Readonly<Record<string, string>>) {
  const leads = columns.flatMap((c) => c.leads).sort(byNewest);
  return columns.map((column) => ({
    stage: column.stage,
    hidden: Math.max(column.stage.lead_count - column.leads.length, 0),
    leads: leads.filter((lead) => (overrides[lead.id] ?? lead.stage.id) === column.stage.id),
  }));
}

function BoardSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto p-4" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="w-72 shrink-0 space-y-3 rounded-lg bg-sunken p-3">
          <Skeleton className="h-5 w-[120px]" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ))}
    </div>
  );
}

/** Pipeline board: drag cards between stage columns, or use the card's keyboard alternative. */
export function LeadsBoard({ id, columns, isPending, canEdit, overrides, leadHref, onMove }: LeadsBoardProps) {
  const { t } = useTranslation();
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  if (isPending || !columns) return <BoardSkeleton />;
  const grouped = regroup(columns, overrides);
  const stages = columns.map((c) => c.stage);
  const allLeads = grouped.flatMap((g) => g.leads);

  const onDrop = (event: DragEvent<HTMLElement>, stage: Stage) => {
    event.preventDefault();
    setDropTarget(null);
    const lead = allLeads.find((l) => l.id === event.dataTransfer.getData(DRAG_TYPE));
    if (lead && (overrides[lead.id] ?? lead.stage.id) !== stage.id) onMove(lead, stage);
  };

  return (
    <div id={id} role="tabpanel" aria-label={t("engage.leads.board.label")}>
      <p className="min-h-7 px-4 pt-3 text-xs text-fg-subtle">{canEdit ? t("engage.leads.board.dragHint") : null}</p>
      <div className="flex gap-4 overflow-x-auto p-4">
        {grouped.map((column) => (
          <section
            key={column.stage.id}
            aria-label={t("engage.leads.board.column", { stage: column.stage.name, count: column.leads.length + column.hidden })}
            data-testid={`board-column-${column.stage.name}`}
            className={cn(
              "flex w-72 shrink-0 flex-col rounded-lg border border-line bg-sunken",
              dropTarget === column.stage.id && "border-primary ring-2 ring-primary/30",
            )}
            onDragOver={(event) => {
              if (!canEdit || !event.dataTransfer.types.includes(DRAG_TYPE)) return;
              event.preventDefault();
              setDropTarget(column.stage.id);
            }}
            onDragLeave={() => {
              setDropTarget((current) => (current === column.stage.id ? null : current));
            }}
            onDrop={(event) => {
              onDrop(event, column.stage);
            }}
          >
            <h3 className="flex items-center justify-between gap-2 px-3 pb-2 pt-3 text-sm font-bold">
              <Badge tone={stageTone(column.stage)}>{column.stage.name}</Badge>
              <span className="text-xs font-semibold text-fg-subtle">{column.leads.length + column.hidden}</span>
            </h3>
            <ul className="flex min-h-24 flex-1 flex-col gap-2 px-3 pb-3">
              {column.leads.map((lead) => (
                <li key={lead.id}>
                  <LeadCard
                    lead={lead}
                    stageId={overrides[lead.id] ?? lead.stage.id}
                    stages={stages}
                    canEdit={canEdit}
                    href={leadHref(lead.id)}
                    dragType={DRAG_TYPE}
                    onMove={(stage) => {
                      onMove(lead, stage);
                    }}
                  />
                </li>
              ))}
              {column.leads.length === 0 ? <li className="rounded-md border border-dashed border-line-strong px-3 py-6 text-center text-xs text-fg-subtle">{t("engage.leads.board.empty")}</li> : null}
              {column.hidden > 0 ? <li className="text-center text-xs text-fg-subtle">{t("engage.leads.board.more", { count: column.hidden })}</li> : null}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
